import React, { FC, useContext, useEffect, useState } from 'react'

import { sumBy } from 'lodash'

import MainModal from './common/MainModal'
import { AddItemSkeleton } from './common/AddItemSkeleton'
import { AddItemAccordion } from './common/AddItemAccordion'

import { HandleModalContext } from '../../layout/mainlayout/MainLayout'
import FirestoreService from '../../services/firestoreService'
import EventReviewPublishService from '../../services/firebase-service/event-review-publish/EventReviewPublishService'
import EventRegisteredUsersService from '../../services/firebase-service/event-registered-users/EventRegisteredUsersService'

import helpers from '../../commonHelpers/helpers'
import { getFloatPrice } from '../../helpers/price'
import { getConvertedData } from '../../models/interface.helper'
import { getUserFullName } from '../../helpers/helpers'
import { httpService } from '../../services/httpService'
import useToasterHelper from '../../helpers/ToasterHelper'

import { RegistrationTicketModel } from '../../models/registration-tickets/registrationTicket.model'
import { IEventReviewPublish } from '../../models/event-review-publish/event-review-publish.interface'

import { TicketType } from '../../models/event-ticketing/event-ticketing.interface'
import { IRegistrationTicketInterface } from '../../models/registration-tickets/registrationTicket.interface'
import { TEventRegisteredUsers } from '../../models/event-registered-users/event-registered-users.interface'
import { IUserInterface } from '../../models/users/user.interface'

import { MODAL_CONSTS } from '../../const/modal-const'
import { CONST } from '../../const/const'
import { PAYMENT_STATUS, PAYMENT_TYPE } from '../../types/payment'

const COLLECTIONS = CONST.DATA.FIRESTORE.V01.COLLECTIONS

interface AddTicketsProps {
  dataToPassOn: {
    eventId: string
    registrationDocId: string
    user: IUserInterface
    publishedEvent: IEventReviewPublish
    onSave?: (value: IRegistrationTicketInterface[]) => void
  }
}

export const AddTickets: FC<AddTicketsProps> = ({ dataToPassOn }) => {
  const toastFunctions = useToasterHelper()

  const { eventId, registrationDocId, user, publishedEvent, onSave } = dataToPassOn

  const [buttonLoading, setButtonLoading] = useState(false)
  const [loading, setLoading] = useState(false)
  const [tickets, setTickets] = useState<(TicketType & { selectedCount?: number })[]>([])
  const [selectedTickets, setSelectedTickets] = useState<
    (TicketType & { selectedCount?: number })[]
  >([])
  const [registeredUser, setRegisteredUser] = useState<TEventRegisteredUsers | null>(null)

  const handleModal = useContext(HandleModalContext)?.handleModal

  useEffect(() => {
    const getData = async () => {
      try {
        setLoading(true)

        const publishedEvent = await EventReviewPublishService.getById(eventId)
        const registrationInDb = await EventRegisteredUsersService.getById(registrationDocId)

        setTickets(
          publishedEvent?.EventTickets?.sellSpectatorTickets
            ? publishedEvent?.EventTickets?.tickets || []
            : []
        )
        setRegisteredUser(registrationInDb)
      } catch (e) {
      } finally {
        setLoading(false)
      }
    }
    getData().then()
  }, [eventId, registrationDocId])

  const onSubmit = async () => {
    const ticketsToCreate: IRegistrationTicketInterface[] = []

    try {
      setButtonLoading(true)
      selectedTickets.forEach((ticket) => {
        for (let i = 0; i < (ticket.selectedCount || 1); i++) {
          ticketsToCreate.push(
            getConvertedData({
              ...new RegistrationTicketModel({
                userId: registeredUser?.userId ?? null,
                userName: registeredUser?.userName ?? null,
                refundId: null,
                refundDocId: null,
                amountRefunded: 0,
                amountScratched: 0,
                ticketItemId: ticket.uuid,

                paymentStatus: PAYMENT_STATUS.DEFAULT,
                paymentType: PAYMENT_TYPE.CASH,

                eventId: registeredUser?.eventId || null,
                eventName: registeredUser?.eventName || null,
                selectedUnitsCount: 1,
                registrationDocId: registeredUser?.id || null,
                ticketTitle: ticket.name,
                ticketPrice: ticket.cost || 0,
                includeSalesTax: publishedEvent?.EventPaymentSettings?.chargeSalesTax || false,

                recipientId: user.id,
                recipientName: getUserFullName(user),
                recipientProfilePicture: user.userProfilePicture || null,

                ownerId: user.id,
                ownerName: getUserFullName(user),
                ownerProfilePicture: user.userProfilePicture || null,
              }).toObject(),
            })
          )
        }
      })

      await helpers.asyncWhileLoop({
        loopCount: ticketsToCreate.length,
        functionToFire: async (index) => {
          ticketsToCreate[index].id = (
            await FirestoreService.createItem(
              COLLECTIONS.REGISTRATION_TICKET.NAME,
              new RegistrationTicketModel({
                ...ticketsToCreate[index],
              }).toFirestore()
            )
          ).id
        },
      })

      await httpService({
        url: 'update_recipient',
        data: { registration: registeredUser, payer: user },
        method: 'POST',
      })

      if (onSave) onSave(ticketsToCreate)

      handleModal?.(false, MODAL_CONSTS.ADD_TICKETS)
    } catch (error) {
      // ROLLBACK if update_recipient failed
      await helpers.asyncWhileLoop({
        loopCount: ticketsToCreate.length,
        functionToFire: async (index) => {
          await FirestoreService.deleteItem(
            COLLECTIONS.REGISTRATION_TICKET.NAME,
            ticketsToCreate[index].id!
          )
        },
      })
      console.error('error:', error)
      toastFunctions.error({
        message: 'Add ticket failed',
      })
    } finally {
      setButtonLoading(false)
    }
  }

  const onChange = (id: string, value: number) => {
    const ticketsUpdated = tickets.map((item) => {
      if (item.uuid === id) {
        return { ...item, selectedCount: value || 0 }
      } else {
        return item
      }
    })
    setTickets([...ticketsUpdated])
    setSelectedTickets([...ticketsUpdated.filter((item) => (item.selectedCount || 0) > 0)])
  }

  const totalSelected = sumBy(selectedTickets, (item) => item.selectedCount || 1)
  const totalPrice = getFloatPrice(
    sumBy(selectedTickets, (item) => item.cost * (item.selectedCount || 1))
  )

  return (
    <MainModal
      title="Add tickets"
      show={true}
      type="ADD_TICKETS"
      size="lg"
      className="flex flex-col flex-1"
      buttons={[
        {
          label: selectedTickets?.[0]
            ? `Add ${totalSelected} Tickets, ${totalPrice}`
            : 'Add Tickets',
          fullWidth: true,
          loading: buttonLoading,
          disabled: buttonLoading || !selectedTickets?.[0],
          onClick: onSubmit,
        },
        {
          label: 'Cancel',
          bgClass: 'bg-SeabiscuitLightThemeColor',
          borderClass: 'border border-transparent',
          textClass: 'text-SeabiscuitIconThemeColor/50',
          fullWidth: true,
          disabled: buttonLoading,
          onClick: () => handleModal?.(false, MODAL_CONSTS.ADD_TICKETS),
        },
      ]}
    >
      <div className="flex flex-col flex-1 w-full pt-4 h-full text-SeabiscuitDark200ThemeColor">
        {loading ? (
          <AddItemSkeleton />
        ) : (
          <AddItemAccordion
            name="tickets"
            items={tickets.map((ticket) => ({
              id: ticket.uuid,
              name: ticket.name,
              cost: ticket.cost,
              available: ticket.available,
              selectedCount: ticket.selectedCount || 0,
            }))}
            selectedItems={selectedTickets.map((ticket) => ({
              id: ticket.uuid,
              name: ticket.name,
              cost: ticket.cost,
              available: ticket.available,
              selectedCount: ticket.selectedCount || 0,
            }))}
            onChange={onChange}
          />
        )}
      </div>
    </MainModal>
  )
}
